<template>
  <BorderGradient :class="borderGradientClasses"
                  gradient-class="my-6 h-72 border-2 bg-white transition-all"
                  :border-radius="1">
    <div :class="borderGradientChildClasses"
         class="flex h-full w-full flex-col items-center justify-center rounded-2xl bg-white transition-all">
      <div class="flex space-x-4 items-center">
        <img src="https://resources.businessdraft.com/pdf-file-icon.svg"
             alt="PDF icon" class="h-12 py-2"/>
        <img src="https://resources.businessdraft.com/word-icon.svg"
             alt=".doc and .docx icon" class="h-12 py-2"/>
        <ColoredTxtSvg :backgroundHex="backgroundHex" :foregroundHex="foregroundHex"/>
      </div>
      <span class="mt-2 text-lg font-medium text-slate-800">
            Drag and drop your resume
        </span>
      <span class="mt-2 flex items-center font-medium text-slate-500 space-x-2">
            <span class="block h-px w-12 bg-gradient-to-r from-slate-50 to-slate-300"/>
            <span>or</span>
            <span class="block h-px w-12 bg-gradient-to-l from-slate-50 to-slate-300"/>
        </span>
      <button id="browseFilesButton"
              type="button" @click="clickHiddenInput" :disabled="uploading || googleLoading"
              :class="!(backgroundHex && foregroundHex) && 'text-slate-600 bg-slate-200 hover:text-slate-600'"
              :style="backgroundHex && foregroundHex && `background-color: ${backgroundHex}; color: ${foregroundHex};`"
              class="mx-auto mt-4 flex cursor-pointer items-center rounded-md px-6 font-medium transition-all py-2.5 space-x-2 hover:brightness-95">
        <DevicePhoneMobileIcon v-if="isMobile" class="h-4 w-4"/>
        <ComputerDesktopIcon v-else class="h-4 w-4"/>
        <span>Browse Files on My Device</span>
      </button>
      <GooglePicker
        mimes="application/pdf,application/vnd.google-apps.document,application/msword,text/plain,text/rtf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        :file="file"
        :data-url="fileDataUrl"
        @update:file="updateFile"
        v-model:loading="googleLoading"
        filename="google-drive-uploaded-resume.pdf" should-export-as-pdf>
          <span
            class="mx-auto mt-2 flex cursor-pointer items-center rounded-md px-6 font-medium text-slate-600 transition-all bg-bdBlue-100 py-2.5 space-x-2 hover:bg-bdBlue-200 hover:text-slate-600 focus:bg-bdBlue-300">
            <AnimationSpinningLoader v-if="googleLoading" diameter="1rem" color="#3e6bb7" class="h-4 w-4"/>
            <img v-else src="https://resources.businessdraft.com/google-drive-icon.svg"
                 alt="Google Drive Icon" class="h-4 w-4"/>
            <span>Upload from Google Drive</span>
          </span>
      </GooglePicker>
    </div>
  </BorderGradient>

  <input
    ref="fileInput"
    name="resumeUploadInput"
    type="file"
    hidden
    class="hidden"
    @change="inputFileChange"
    accept=".pdf,.docx,.doc,.txt,.rtf"
  />
</template>

<script setup>
import AnimationSpinningLoader from "./AnimationSpinningLoader.vue";
import BorderGradient from "./BorderGradient.vue";
import GooglePicker from "./GooglePicker.vue";
import {ComputerDesktopIcon, DevicePhoneMobileIcon} from "@heroicons/vue/24/solid";
import {computed, onBeforeUnmount, onMounted, ref} from "vue";
import ColoredTxtSvg from "./ColoredTxtSvg.vue";

const emits = defineEmits(["update:file", "update:fileDataUrl", "update:fileName", "update:formattedFileSize"]);
const props = defineProps({
  file: Object,
  fileDataUrl: String,
  uploading: Boolean,
  backgroundHex: String,
  foregroundHex: String,
  fileName: String,
  formattedFileSize: String,
});

const googleLoading = ref(false);
const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
const allowedFileTypes = ["application/pdf", "application/msword", "text/plain", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "text/rtf"];

let dragLeaveTimeout = null;

const isHovering = ref(false);
const fileInput = ref(null);

function createPreviewDataUrl(file) {
  if (!file || allowedFileTypes.indexOf(file.type) === -1) return;

  const reader = new FileReader();
  reader.readAsDataURL(file);

  reader.onload = (e) => {
    emits("update:fileDataUrl", e.target.result);
  };

  reader.onerror = (e) => console.error(e);
}

const handleFileUpload = (files = []) => {
  if (files.length === 0) return;
  const file = files[0];

  if (allowedFileTypes.indexOf(file.type) === -1) {
    toast.error("Only .pdf, .doc, .docx, .txt, and .rtf files are supported.");
    return;
  }

  updateFile(file);
};

const inputFileChange = (e) => {
  const files = e.target.files || [];
  handleFileUpload(files);
  e.target.value = "";
};

const handleDragOver = (e) => {
  e.preventDefault();
  e.stopPropagation();

  isHovering.value = true;
  clearTimeout(dragLeaveTimeout);
};

const handleDragLeave = (e) => {
  e.preventDefault();
  e.stopPropagation();

  dragLeaveTimeout = setTimeout(() => {
    isHovering.value = false;
  }, 100);
};

const handleFileDrop = (e) => {
  e.preventDefault();
  e.stopPropagation();

  const files = e.dataTransfer?.files || [];
  handleFileUpload(files);
  isHovering.value = false;
};

const clickHiddenInput = () => {
  fileInput.value.click();
};
const borderGradientClasses = computed(() => {
  if (!isHovering.value) return "border-dashed border-slate-200";
  return "bg-gradient border-none";
});

const borderGradientChildClasses = computed(() => {
  if (!isHovering.value) return "hover:bg-opacity-90";
  return "bg-opacity-90";
});

const calcFileName = (file) => {
  const fileName = file?.name || "Resume File";
  emits("update:fileName", fileName);
};

const calcFormattedFileSize = (file) => {
  const size = file?.size || 0;

  const kb = size / 1000;
  if (kb < 1000) return emits("update:formattedFileSize", `${kb.toFixed(2)}kb`);

  const mb = kb / 1000;
  if (mb < 1000) emits("update:formattedFileSize", `${mb.toFixed(2)}mb`);

  const gb = mb / 1000;
  emits("update:formattedFileSize", `${gb.toFixed(2)}gb`);
};

const updateFile = (file) => {
  calcFileName(file);
  createPreviewDataUrl(file);
  calcFormattedFileSize(file);
  emits("update:file", file);
};

onMounted(() => {
  if (props.file && !props.fileDataUrl) createPreviewDataUrl(props.file);
  if (props.file && !props.fileName) calcFileName(props.file);
  if (props.file && !props.formattedFileSize) calcFormattedFileSize(props.file);

  window.addEventListener("dragover", handleDragOver);
  window.addEventListener("dragleave", handleDragLeave);
  window.addEventListener("drop", handleFileDrop);
});

onBeforeUnmount(() => {
  window.removeEventListener("dragover", handleDragOver);
  window.removeEventListener("dragleave", handleDragLeave);
  window.removeEventListener("drop", handleFileDrop);
});
</script>
